import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Trophy, Download, CheckCircle2, AlertTriangle, DollarSign, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * /prize-pool — Season 3 payout dashboard (§20-§32).
 * Summary + player payout table + team-owner prizes + CSV export.
 */
export default function PrizePool() {
  const [summary, setSummary] = useState(null);
  const [payouts, setPayouts] = useState({ rows: [], totals: {} });
  const [ownerPrizes, setOwnerPrizes] = useState({ rows: [], total: 0 });
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const [s, p, o] = await Promise.all([
        api.get("/prize-pool/summary"),
        api.get("/prize-pool/player-payouts"),
        api.get("/prize-pool/team-owner-payouts").catch(() => ({ data: { rows: [], total: 0 } })),
      ]);
      setSummary(s.data); setPayouts(p.data); setOwnerPrizes(o.data);
    } catch (e) {
      toast.error("Failed to load prize pool data");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return payouts.rows.filter(r => {
      if (filter === "champion" && r.final_position !== 1) return false;
      if (filter === "runner_up" && r.final_position !== 2) return false;
      if (filter === "others" && (r.final_position === 1 || r.final_position === 2)) return false;
      if (filter === "reg_unpaid" && r.registration_fee_paid) return false;
      if (filter === "pending" && r.payment_status !== "pending") return false;
      if (filter === "paid" && r.payment_status !== "paid") return false;
      if (!term) return true;
      return (r.player_name || "").toLowerCase().includes(term) || (r.team_name || "").toLowerCase().includes(term);
    });
  }, [payouts.rows, filter, q]);

  const downloadCSV = () => {
    const base = process.env.REACT_APP_BACKEND_URL;
    window.open(`${base}/api/prize-pool/export.csv`, "_blank");
  };

  const markStatus = async (playerId, status) => {
    try {
      await api.put(`/prize-pool/player-payouts/${playerId}`, { payment_status: status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const fmt = (n) => `KSh ${Number(n || 0).toLocaleString()}`;

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="prize-pool">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Season 3 · Finance</div>
          <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
            Prize<br/><span className="text-[#F4B400]">Pool.</span>
          </h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={downloadCSV} className="bg-[#00A86B] hover:bg-[#00935A] text-white" data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={load} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="refresh-btn">Refresh</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="summary-cards">
          <SummaryCard label="Total Prize Pool" value={fmt(summary.prize_pool_total)} accent="#F4B400" />
          <SummaryCard label="Team Owner Prizes" value={fmt(summary.team_owner_prizes_total)} accent="#7DD3FC" />
          <SummaryCard label="Player Bonuses" value={fmt(summary.player_bonuses_total)} accent="#00A86B" />
          <SummaryCard label="Unallocated" value={fmt(summary.unallocated_balance)} accent={summary.unallocated_balance < 0 ? "#E32424" : "#94A3B8"} testid="unallocated-card" />
        </div>
      )}

      {summary?.player_totals && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-8">
          <MiniStat label="Players" value={summary.player_totals.total_players} icon={Users} />
          <MiniStat label="Auction $" value={fmt(summary.player_totals.total_auction_money)} icon={DollarSign} />
          <MiniStat label="Winner Bonuses" value={fmt(summary.player_totals.total_winner_bonuses)} icon={Trophy} />
          <MiniStat label="Reg Deductions" value={fmt(summary.player_totals.total_registration_deductions)} icon={AlertTriangle} />
          <MiniStat label="Net Outflow" value={fmt(summary.player_totals.total_net_payable)} icon={CheckCircle2} accent="#F4B400" />
        </div>
      )}

      {ownerPrizes.rows.length > 0 && (
        <Card className="mb-8 p-4 bg-[#0f1a15] border-white/10">
          <div className="font-anton text-2xl uppercase text-white mb-3">Team Owner Prizes</div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {ownerPrizes.rows.map(r => (
              <div key={r.team_id} className="p-3 rounded-lg bg-[#11241b] border border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-[#F4B400]">{r.prize_key.replace(/_/g, " ")}</div>
                <div className="font-anton text-lg text-white truncate">{r.team_name}</div>
                <div className="font-mono text-[#00A86B] text-sm mt-1">{fmt(r.prize_amount)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {[["all","All"],["champion","Champion"],["runner_up","Runner-Up"],["others","3rd–13th"],["reg_unpaid","Reg Unpaid"],["pending","Pending"],["paid","Paid"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`filter-${k}`}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold rounded-full transition ${
              filter === k ? "bg-[#F4B400] text-black" : "bg-[#11241b] text-zinc-400 hover:text-white border border-white/10"
            }`}>{label}</button>
        ))}
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="ml-auto max-w-xs bg-[#11241b] border-white/10 text-white" data-testid="search-input" />
      </div>

      <Card className="p-0 bg-[#0f1a15] border-white/10 overflow-hidden" data-testid="payout-table">
        <div className="overflow-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="bg-[#11241b] text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0">
              <tr>
                <th className="text-left p-3">Player</th>
                <th className="text-left p-3">Team</th>
                <th className="text-center p-3">Pos</th>
                <th className="text-right p-3">Auction</th>
                <th className="text-right p-3">Bonus</th>
                <th className="text-right p-3">Deduction</th>
                <th className="text-right p-3">Net Payable</th>
                <th className="text-center p-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredRows.map(r => (
                <tr key={r.player_id} className="hover:bg-white/5" data-testid={`row-${r.player_id}`}>
                  <td className="p-3">
                    <div className="text-white font-bold truncate max-w-[220px]">{r.player_name}</div>
                    <div className="text-[10px] text-zinc-500">{r.email || <span className="text-red-500">no email</span>}</div>
                  </td>
                  <td className="p-3 text-zinc-300">{r.team_name || "—"}</td>
                  <td className="p-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                      r.final_position === 1 ? "bg-[#F4B400] text-black" :
                      r.final_position === 2 ? "bg-[#7DD3FC] text-black" :
                      "bg-white/10 text-zinc-400"
                    }`}>{r.final_position || "—"}</span>
                  </td>
                  <td className="p-3 text-right font-mono text-white">{r.auction_value.toLocaleString()}</td>
                  <td className="p-3 text-right font-mono text-[#00A86B]">{r.bonus > 0 ? `+${r.bonus.toLocaleString()}` : "—"}</td>
                  <td className="p-3 text-right font-mono text-red-400">{r.registration_deduction > 0 ? `-${r.registration_deduction.toLocaleString()}` : "—"}</td>
                  <td className="p-3 text-right font-mono text-[#F4B400] font-bold">{r.net_payable.toLocaleString()}</td>
                  <td className="p-3 text-center">
                    <select value={r.payment_status} onChange={(e) => markStatus(r.player_id, e.target.value)}
                      data-testid={`status-${r.player_id}`}
                      className="bg-[#060d09] border border-white/10 text-white text-xs px-2 py-1 rounded">
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                      <option value="payment_failed">Failed</option>
                      <option value="on_hold">On Hold</option>
                    </select>
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && (
                <tr><td colSpan="8" className="p-8 text-center text-zinc-500">No players match this filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      {busy && <div className="text-zinc-500 text-sm mt-3">Loading…</div>}
    </div>
  );
}

function SummaryCard({ label, value, accent, testid }) {
  return (
    <div className="p-4 rounded-xl bg-[#11241b] border-l-4" style={{ borderLeftColor: accent }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-anton text-2xl text-white leading-none mt-1">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent = "#94A3B8" }) {
  return (
    <div className="p-3 rounded-lg bg-[#11241b] border border-white/10 flex items-center gap-3">
      <Icon className="w-5 h-5" style={{ color: accent }} />
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
        <div className="font-mono text-sm text-white truncate">{value}</div>
      </div>
    </div>
  );
}
